import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCart } from '../context/CartContext';
import AnnouncementBanner from './AnnouncementBanner';
import BluesparkLogo from './BluesparkLogo';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const cartCount = getCartCount();

  return (
    <header className="sticky top-0 z-50 w-full bg-black text-white shadow-sm">
      <AnnouncementBanner />
      <div className="border-b border-white/10">
        <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="header-logo-link">
            <BluesparkLogo tone="light" height={42} />
          </Link>

          {/* Desktop Navigation with Dropdowns */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link to="/" className="text-sm font-medium text-white hover:text-blue-400 transition-colors px-4 py-2">
                    Home
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium bg-transparent text-white hover:text-blue-400 hover:bg-white/5 focus:bg-white/5 data-[state=open]:bg-white/5 data-[state=open]:text-blue-400">
                  Shop by Make
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[400px] gap-3 p-4">
                    <div className="grid grid-cols-2 gap-2">
                      <Link to="/products?make=Audi" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">Audi</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Audi tuning modules
                        </p>
                      </Link>
                      <Link to="/products?make=BMW" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">BMW</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          BMW tuning specialists
                        </p>
                      </Link>
                      <Link to="/products?make=Mercedes" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">Mercedes</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Mercedes performance
                        </p>
                      </Link>
                      <Link to="/products?make=Volkswagen" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">Volkswagen</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          VW tuning solutions
                        </p>
                      </Link>
                      <Link to="/products?make=Mini" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">Mini</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Mini Cooper tuning
                        </p>
                      </Link>
                      <Link to="/products?make=MG" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">MG</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          MG performance chips
                        </p>
                      </Link>
                      <Link to="/products?make=Nissan" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">Nissan</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Nissan tuning boxes
                        </p>
                      </Link>
                      <Link to="/products?make=Mazda" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">Mazda</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Mazda tuning modules
                        </p>
                      </Link>
                      <Link to="/products?make=Hyundai" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">Hyundai</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Hyundai performance
                        </p>
                      </Link>
                      <Link to="/products?make=Skoda" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium leading-none">Skoda</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Skoda tuning chips
                        </p>
                      </Link>
                    </div>
                    <Link to="/products" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors bg-blue-50 hover:bg-blue-100">
                      <div className="text-sm font-bold text-blue-600">View All Vehicles →</div>
                    </Link>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium bg-transparent text-white hover:text-blue-400 hover:bg-white/5 focus:bg-white/5 data-[state=open]:bg-white/5 data-[state=open]:text-blue-400">
                  Petrol Tuning
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[350px] gap-3 p-4">
                    <Link to="/products?category=pro-petrol" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Pro Petrol Tuning</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        Standard plug & play petrol modules
                      </p>
                    </Link>
                    <Link to="/products?category=3-channel" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">3 Channel Pro</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        Advanced 3-channel petrol tuning
                      </p>
                    </Link>
                    <Link to="/products?category=petrol-sent" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Pro Petrol SENT</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        SENT protocol compatible modules
                      </p>
                    </Link>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium bg-transparent text-white hover:text-blue-400 hover:bg-white/5 focus:bg-white/5 data-[state=open]:bg-white/5 data-[state=open]:text-blue-400">
                  Diesel Tuning
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[350px] gap-3 p-4">
                    <Link to="/products?category=pro-diesel" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Pro Diesel Tuning</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        Standard diesel tuning modules
                      </p>
                    </Link>
                    <Link to="/products?category=pro-boost" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Pro + Boost</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        Enhanced boost control for diesel
                      </p>
                    </Link>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium bg-transparent text-white hover:text-blue-400 hover:bg-white/5 focus:bg-white/5 data-[state=open]:bg-white/5 data-[state=open]:text-blue-400">
                  Info & FAQ
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[400px] gap-3 p-4">
                    <Link to="/faq/installation" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Installation Guide</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        Step-by-step DIY installation instructions
                      </p>
                    </Link>
                    <Link to="/faq/general" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">General FAQs</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        Common questions about tuning boxes
                      </p>
                    </Link>
                    <Link to="/faq/warranty" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Warranty & Returns</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        2-year warranty & 30-day money back guarantee
                      </p>
                    </Link>
                    <Link to="/faq/compatibility" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Vehicle Compatibility</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        Check if your vehicle is compatible
                      </p>
                    </Link>
                    <Link to="/faq/bluetooth" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Bluetooth App Guide</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        How to use the Bluetooth tuning app
                      </p>
                    </Link>
                    <Link to="/faq/shipping" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Shipping & Delivery</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        Australia-wide shipping information
                      </p>
                    </Link>
                    <Link to="/faq/contact" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                      <div className="text-sm font-medium leading-none">Contact Us</div>
                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                        Get in touch with our team
                      </p>
                    </Link>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <form onSubmit={handleSearch} className="hidden lg:flex items-center flex-1 max-w-md mx-4">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by vehicle make or model..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 bg-white/10 border-white/15 text-white placeholder:text-gray-400 focus-visible:ring-blue-400 focus-visible:ring-offset-0"
                data-testid="header-search-desktop"
              />
            </div>
          </form>

          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="icon"
              className="relative text-white hover:text-blue-400 hover:bg-white/10"
              onClick={() => navigate('/cart')}
              data-testid="header-cart-btn"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Button>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-white hover:text-blue-400 hover:bg-white/10">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col space-y-1 mt-6">
                  <Link to="/" className="text-base font-medium hover:text-blue-600 transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                    Home
                  </Link>
                  <Link to="/products" className="text-base font-medium hover:text-blue-600 transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                    All Products
                  </Link>

                  <div className="pt-3 mt-2 border-t">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Shop by Make</p>
                    {['Audi','BMW','Mercedes','Volkswagen','Ford','Nissan','Toyota','Mazda','Volvo','Other'].map((m) => (
                      <Link
                        key={m}
                        to={`/products?make=${m}`}
                        className="block text-sm py-1.5 text-gray-700 hover:text-blue-600"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {m}
                      </Link>
                    ))}
                  </div>

                  <div className="pt-3 mt-2 border-t">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Tuning</p>
                    <Link to="/products?category=pro-petrol" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>
                      Petrol Tuning
                    </Link>
                    <Link to="/products?category=pro-diesel" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>
                      Diesel Tuning
                    </Link>
                    <Link to="/products?category=pro-boost" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>
                      Pro + Boost
                    </Link>
                  </div>

                  <div className="pt-3 mt-2 border-t">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Info & FAQ</p>
                    <Link to="/faq/general" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>General FAQs</Link>
                    <Link to="/faq/installation" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Installation Guide</Link>
                    <Link to="/faq/warranty" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Warranty & Returns</Link>
                    <Link to="/faq/compatibility" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Vehicle Compatibility</Link>
                    <Link to="/faq/bluetooth" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Bluetooth App Guide</Link>
                    <Link to="/faq/shipping" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Shipping & Delivery</Link>
                    <Link to="/faq/contact" className="block text-sm py-1.5 text-gray-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Contact Us</Link>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <form onSubmit={handleSearch} className="lg:hidden pb-3">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search vehicles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 bg-white/10 border-white/15 text-white placeholder:text-gray-400 focus-visible:ring-blue-400 focus-visible:ring-offset-0"
              data-testid="header-search-mobile"
            />
          </div>
        </form>
      </div>
      </div>
    </header>
  );
};

export default Header;
